/**
 * Centralized form guard for MassagerPage
 * Handles form change detection for BOTH MassagerDetailModal and MassagerBulkSettingsModal
 * Uses a single React Router blocker to avoid "one blocker at a time" conflicts
 */

import { create } from 'zustand';
import { useFormConfirmationBlocker } from '@/hooks/useFormConfirmationBlocker';
import useAppModal from '../useAppModal';
import { FORM_MESSAGES } from '@/constants';

interface FormGuardState {
  // Track form changes for each modal
  detailModalHasChanges: boolean;
  bulkSettingsModalHasChanges: boolean;

  // Update functions called by the modals
  setDetailModalChanges: (hasChanges: boolean) => void;
  setBulkSettingsModalChanges: (hasChanges: boolean) => void;

  // Clear all form changes (called after successful save or discard)
  clearAllChanges: () => void;

  // Check if ANY modal has form changes
  hasAnyChanges: () => boolean;
}

// Zustand store for managing form state across modals
const useFormGuardStore = create<FormGuardState>((set, get) => ({
  detailModalHasChanges: false,
  bulkSettingsModalHasChanges: false,

  setDetailModalChanges: (hasChanges: boolean) => {
    set({ detailModalHasChanges: hasChanges });
  },

  setBulkSettingsModalChanges: (hasChanges: boolean) => {
    set({ bulkSettingsModalHasChanges: hasChanges });
  },

  clearAllChanges: () => {
    set({ detailModalHasChanges: false, bulkSettingsModalHasChanges: false });
  },

  hasAnyChanges: () => {
    const state = get();
    return state.detailModalHasChanges || state.bulkSettingsModalHasChanges;
  },
}));

/**
 * Hook used by MassagerDetailModal to report form changes
 */
export const useDetailModalFormGuard = () => {
  const { setDetailModalChanges } = useFormGuardStore();
  const { confirm } = useAppModal();

  const confirmBeforeAction = (onConfirm: () => void) => {
    confirm({
      title: FORM_MESSAGES.DISCARD_CHANGES_TITLE,
      content: FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION,
      okText: FORM_MESSAGES.CONFIRM_OK,
      cancelText: FORM_MESSAGES.CONFIRM_CANCEL,
      okButtonProps: { danger: true },
      onOk: () => {
        setDetailModalChanges(false);
        onConfirm();
      },
    });
  };

  return {
    setFormChanged: setDetailModalChanges,
    confirmBeforeAction,
  };
};

/**
 * Hook used by MassagerBulkSettingsModal to report form changes
 */
export const useBulkSettingsModalFormGuard = () => {
  const { setBulkSettingsModalChanges } = useFormGuardStore();
  const { confirm } = useAppModal();

  const confirmBeforeAction = (onConfirm: () => void) => {
    confirm({
      title: FORM_MESSAGES.DISCARD_CHANGES_TITLE,
      content: FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION,
      okText: FORM_MESSAGES.CONFIRM_OK,
      cancelText: FORM_MESSAGES.CONFIRM_CANCEL,
      okButtonProps: { danger: true },
      onOk: () => {
        setBulkSettingsModalChanges(false);
        onConfirm();
      },
    });
  };

  return {
    setFormChanged: setBulkSettingsModalChanges,
    confirmBeforeAction,
  };
};

/**
 * Centralized page-level hook for MassagerPage
 * Registers a SINGLE blocker that protects both modals
 * Reuses the same confirmation logic as useFormChangeGuard
 */
export const useMassagerPageFormGuard = () => {
  const hasAnyChanges = useFormGuardStore(state => state.hasAnyChanges());
  const clearAllChanges = useFormGuardStore(state => state.clearAllChanges);

  // Reuse the common blocker + confirmation logic
  useFormConfirmationBlocker(hasAnyChanges, clearAllChanges);

  return {
    hasAnyChanges,
    clearAllChanges,
  };
};
